import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OrderQuantitySelector } from "./OrderQuantitySelector";
import { formatCentsAsUsd } from "@/lib/money";

interface Product {
  _id: string;
  name?: string;
  /** Integer cents */
  price: number;
  payWhatYouCan?: boolean;
}

interface CheckoutEmailStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  /** Cart subtotal in integer cents */
  total: number;
  amountToPay?: string;
  onAmountToPayChange?: (amount: string) => void;
}

export function CheckoutEmailStep({
  email,
  onEmailChange,
  products,
  quantities,
  onQuantityChange,
  total,
  amountToPay = "",
  onAmountToPayChange,
}: CheckoutEmailStepProps) {
  const hasPayWhatYouCan = products.some((p) => p.payWhatYouCan);
  const displayTotalCents =
    hasPayWhatYouCan && amountToPay
      ? (() => {
          const parsed = parseFloat(amountToPay);
          const cents = Math.round(parsed * 100);
          return !isNaN(parsed) && cents >= total ? cents : total;
        })()
      : total;

  return (
    <div className="space-y-4 flex-1">
      <OrderQuantitySelector
        products={products}
        quantities={quantities}
        onQuantityChange={onQuantityChange}
        total={displayTotalCents}
      />
      <div className="space-y-2">
        <Label htmlFor="customer-email">Email *</Label>
        <Input
          id="customer-email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />
      </div>
      {hasPayWhatYouCan && onAmountToPayChange && (
        <div className="space-y-2">
          <Label htmlFor="amount-to-pay">Amount to pay</Label>
          <div className="flex items-center gap-2">
            <Input
              id="amount-to-pay"
              type="number"
              min={total / 100}
              step="0.01"
              placeholder={formatCentsAsUsd(total)}
              value={amountToPay}
              onChange={(e) => onAmountToPayChange(e.target.value)}
            />
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              (${formatCentsAsUsd(total)} or more)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
